import EditorPopup from '@/components/Editor/EditorPopup';
import { EditStructuredSectionItemModal } from '@/components/SectionStructureForm/EditStructuredSectionItemModal';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useDashboardContext } from '@/contexts/DashboardContext';
import { useSnackbarContext } from '@/contexts/SnackbarContext';
import { UserContext } from '@/contexts/UserContext';
import {
  getItemFromSection,
  WebsiteUpdate,
} from '@/contexts/WebsiteUpdaterContext';
import { auth } from '@/Firebase';
import AddFavoriteSectionModal from '@/modals/AddFavoriteSectionModal';
import AddSectionItemModal from '@/modals/AddSectionItemModal';
import { AddSectionApprovalModal } from '@/modals/AddSectionModal';
import AddSectionToCatalogModal from '@/modals/AddSectionToCatalogModal';
import CreateCTAButtonModal from '@/modals/CreateCTAButtonModal';
import CreateFormModal from '@/modals/CreateFormModal';
import EditHtmlActionModal from '@/modals/EditHtmlActionModal';
import EditQuillSearchParamMentionModal from '@/modals/EditQuillSearchParamMentionModal';
import EditStepperFormModal from '@/modals/EditStepperFormModal/EditStepperFormModal';
import HtmlStepperEditorModal from '@/modals/HtmlStepperEditorModal';
import ImageLibraryModal from '@/modals/ImageLibraryModal/ImageLibraryModal';
import NewGlobalSectionEditorModal from '@/modals/NewGlobalSectionEditorModal';
import ReplaceFormModal from '@/modals/ReplaceFormModal';
import {
  deleteFavoriteSection,
  removeSectionVariantFromCatalog,
} from '@/services/SectionsService';
import { createGlobalSection } from '@/services/WebsitesService';
import * as Sentry from '@sentry/react';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Input from '@superfunnel/ui-core/v2/Input';
import { useContext } from 'react';
import { DynamicPageSettingsModal } from '../ManageDynamicPage/DynamicPageSettingsModal';
import { useEditor } from './EditorContext';

export const EditorModals = () => {
  const { track } = useAnalytics();
  const {
    mode,
    selectedWebsiteId,
    website,
    dynamicPageId,
    selectedVariant,
    activeWebsiteUpdate,
    setActiveWebsiteUpdate,
    updateWebsiteByEvent,
    discardChanges,
    handleGetDataEvent,
    createWebsiteUpdate,
    sidebarOpen,
    setSidebarOpen,

    // Modal states
    settingsModalOpen,
    setSettingsModalOpen,
    addSectionModalOpen,
    setAddSectionModalOpen,
    addFavoriteSectionModalOpen,
    setAddFavoriteSectionModalOpen,
    addSectionToCatalogModalOpen,
    setAddSectionToCatalogModalOpen,
    removeFavoriteSectionModalOpen,
    setRemoveFavoriteSectionModalOpen,
    removeSectionFromCatalogModalOpen,
    setRemoveSectionFromCatalogModalOpen,
    sectionItemEditModalOpen,
    setSectionItemEditModalOpen,
    editFormModalOpen,
    setEditFormModalOpen,
    editHtmlStepperModalOpen,
    setEditHtmlStepperModalOpen,
    selectImageModalOpen,
    setSelectImageModalOpen,
    deleteSectionModalOpen,
    setDeleteSectionModalOpen,
    addSectionItemModalOpen,
    setAddSectionItemModalOpen,
    discardChangesConfirmModalOpen,
    setDiscardChangesConfirmModalOpen,
    editQuillSearchParamMentionModalOpen,
    setEditQuillSearchParamMentionModalOpen,
    beforeCloseEditorWithoutSaveModalOpen,
    setBeforeCloseEditorWithoutSaveModalOpen,
    convertGlobalToLocalModalOpen,
    setConvertGlobalToLocalModalOpen,
    convertLocalToGlobalModalOpen,
    setConvertLocalToGlobalModalOpen,
    editGlobalSectionModalOpen,
    setEditGlobalSectionModalOpen,
    pendingAgentChangesModalOpen,
    setPendingAgentChangesModalOpen,
    editHtmlActionModalOpen,
    setEditHtmlActionModalOpen,
    replaceFormModalOpen,
    setReplaceFormModalOpen,
    editCTAButtonModalOpen,
    setEditCTAButtonModalOpen,
    onClose,
    agentChanges,
    agentDiscardAllChanges,
    agentAcceptAllChanges,
  } = useEditor();

  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const { getSectionListItemFieldConfig: getDashboardSectionConfig } =
    useDashboardContext();

  return (
    <>
      {/* Settings Modal */}
      <DynamicPageSettingsModal
        open={!!settingsModalOpen}
        setOpen={(open) =>
          setSettingsModalOpen(open ? settingsModalOpen : undefined)
        }
        dynamicPageId={settingsModalOpen?.dynamicPageId}
        dynamicPage={settingsModalOpen?.dynamicPage}
      />

      {/* Add Section Modal */}
      <AddSectionApprovalModal
        dynamicPageId={dynamicPageId || ''}
        websiteId={selectedWebsiteId || ''}
        variantId={selectedVariant?.id || ''}
        open={addSectionModalOpen}
        section={activeWebsiteUpdate?.content}
        onClose={() => {
          track('editor_modal_add_section_cancelled');
          setAddSectionModalOpen(false);
        }}
        onAdd={(section) => {
          track('editor_modal_section_added', {
            section_type: section.type,
            section_variant: section.variant,
          });
          setAddSectionModalOpen(false);
          const wu = activeWebsiteUpdate;
          if (!wu) return;
          updateWebsiteByEvent({ ...wu, content: section });
        }}
      />

      {/* Add Favorite Section Modal */}
      <AddFavoriteSectionModal
        dynamicPageId={dynamicPageId || ''}
        open={!!addFavoriteSectionModalOpen}
        section={addFavoriteSectionModalOpen}
        onClose={() => setAddFavoriteSectionModalOpen(undefined)}
      />

      {/* Add Section to Catalog Modal */}
      <AddSectionToCatalogModal
        open={!!addSectionToCatalogModalOpen}
        section={addSectionToCatalogModalOpen}
        onClose={() => setAddSectionToCatalogModalOpen(undefined)}
      />

      {/* Add Section Item Modal */}
      <AddSectionItemModal
        section={
          addSectionItemModalOpen
            ? website?.development.sections.find(
                (i) => i.id === activeWebsiteUpdate?.sectionId,
              )
            : undefined
        }
        open={addSectionItemModalOpen}
        onClose={() => setAddSectionItemModalOpen(false)}
        onAdd={(item) => {
          setAddSectionItemModalOpen(false);
          const wu = activeWebsiteUpdate;
          if (!wu) return;
          updateWebsiteByEvent({ ...wu, content: item });
        }}
      />

      {/* Image Library Modal */}
      <ImageLibraryModal
        open={selectImageModalOpen}
        onClose={() => {
          track('editor_modal_image_library_cancelled');
          setSelectImageModalOpen(false);
        }}
        onSelected={(image) => {
          track('editor_modal_image_selected', {
            image_count: image.length,
          });
          setSelectImageModalOpen(false);
          const wu = activeWebsiteUpdate;
          if (!wu) return;
          updateWebsiteByEvent({ ...wu, content: image[0] });
        }}
        types={
          activeWebsiteUpdate?.type === 'selectThumbnail'
            ? ['image']
            : ['image', 'video']
        }
      />

      {/* Edit Structured Section Item Modal */}
      <EditStructuredSectionItemModal
        open={!!sectionItemEditModalOpen}
        onClose={() => setSectionItemEditModalOpen(undefined)}
        field={getDashboardSectionConfig(
          sectionItemEditModalOpen ?? '',
          activeWebsiteUpdate?.propKey ?? '',
        )}
        item={(
          website?.development.sections?.find(
            (s) => s.id === activeWebsiteUpdate?.sectionId,
          ) as any
        )?.[activeWebsiteUpdate?.propKey ?? '']?.find?.(
          (i: any) => i.id === activeWebsiteUpdate?.content?.id,
        )}
        onSave={(data) => {
          setSectionItemEditModalOpen(undefined);
          const wu = activeWebsiteUpdate;
          if (!wu) return;
          updateWebsiteByEvent({
            ...wu,
            content: { id: wu.content.id, data },
          });
        }}
      />

      {/* Before Close Editor Without Save Modal */}
      <AlertModal
        open={beforeCloseEditorWithoutSaveModalOpen}
        onClose={() => setBeforeCloseEditorWithoutSaveModalOpen(false)}
        title="Are you sure you want to leave without saving?"
        description="If you close the editor without clicking 'Save,' the changes will remain in draft and will not be published to the live site."
        actions={[
          {
            title: 'Cancel',
            onClick: () => setBeforeCloseEditorWithoutSaveModalOpen(false),
            type: 'secondary',
          },
          {
            title: 'Close without saving',
            type: 'primary',
            color: 'primary',
            onClick: () => {
              setBeforeCloseEditorWithoutSaveModalOpen(false);
              onClose();
            },
          },
        ]}
        type={'warn'}
      />

      {/* Delete Section Confirmation Modal */}
      <AlertModal
        open={deleteSectionModalOpen}
        onClose={() => {
          track('editor_modal_delete_section_cancelled');
          setDeleteSectionModalOpen(false);
        }}
        title="Are you sure you want to delete this section?"
        description="This action is not reversible."
        actions={[
          {
            title: 'Cancel',
            onClick: () => {
              track('editor_modal_delete_section_cancelled');
              setDeleteSectionModalOpen(false);
            },
            type: 'secondary',
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: () => {
              track('editor_modal_section_deleted', {
                section_id: activeWebsiteUpdate?.sectionId,
              });
              setDeleteSectionModalOpen(false);
              const wu = activeWebsiteUpdate;
              if (!wu) return;
              updateWebsiteByEvent({ ...wu });
            },
          },
        ]}
        type={'warn'}
      />

      {/* Remove Favorite Section Confirmation Modal */}
      <AlertModal
        open={!!removeFavoriteSectionModalOpen}
        onClose={() => setRemoveFavoriteSectionModalOpen(undefined)}
        title="Are you sure you want to delete this section from the favorites?"
        description="This action is not reversible."
        actions={[
          {
            title: 'Cancel',
            onClick: () => setRemoveFavoriteSectionModalOpen(undefined),
            type: 'secondary',
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            loading: removeFavoriteSectionModalOpen?.loading,
            onClick: () => {
              const favoriteSectionId =
                removeFavoriteSectionModalOpen?.favoriteSectionId;

              if (!selectedAccount || !favoriteSectionId) {
                setRemoveFavoriteSectionModalOpen(undefined);
                return;
              }

              setRemoveFavoriteSectionModalOpen({
                favoriteSectionId,
                loading: true,
              });

              deleteFavoriteSection(
                selectedAccount?.id ?? '',
                favoriteSectionId,
              )
                .then(() =>
                  handleGetDataEvent({ eventName: 'getData', data: {} }),
                )
                .then(() => setRemoveFavoriteSectionModalOpen(undefined))
                .catch((e) => {
                  Sentry.captureException(e);
                  setRemoveFavoriteSectionModalOpen({
                    favoriteSectionId,
                    loading: false,
                  });
                });
            },
          },
        ]}
        type={'warn'}
      />

      {/* Remove Section from Catalog Confirmation Modal */}
      <AlertModal
        open={!!removeSectionFromCatalogModalOpen}
        onClose={() => setRemoveSectionFromCatalogModalOpen(undefined)}
        title="Are you sure you want to delete this section from the catalog?"
        description="This action is not reversible."
        actions={[
          {
            title: 'Cancel',
            onClick: () => setRemoveSectionFromCatalogModalOpen(undefined),
            type: 'secondary',
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            loading: removeSectionFromCatalogModalOpen?.loading,
            onClick: () => {
              const sectionTypeConfigId =
                removeSectionFromCatalogModalOpen?.sectionTypeConfigId;
              const variantId = removeSectionFromCatalogModalOpen?.variantId;

              if (!selectedAccount || !sectionTypeConfigId || !variantId) {
                setRemoveSectionFromCatalogModalOpen(undefined);
                return;
              }

              setRemoveSectionFromCatalogModalOpen({
                ...removeSectionFromCatalogModalOpen,
                loading: true,
              });

              removeSectionVariantFromCatalog(sectionTypeConfigId, variantId)
                .then(() =>
                  handleGetDataEvent({ eventName: 'getData', data: {} }),
                )
                .then(() => setRemoveSectionFromCatalogModalOpen(undefined))
                .catch((e) => {
                  Sentry.captureException(e);
                  setRemoveSectionFromCatalogModalOpen({
                    ...removeSectionFromCatalogModalOpen,
                    loading: false,
                  });
                });
            },
          },
        ]}
        type={'warn'}
      />

      {/* Convert Global to Local Confirmation Modal */}
      <AlertModal
        open={!!convertGlobalToLocalModalOpen}
        onClose={() => {
          track('editor_modal_convert_global_to_local_cancelled');
          setConvertGlobalToLocalModalOpen(false);
          setActiveWebsiteUpdate(undefined);
        }}
        title="Are you sure you want to convert this section to a normal section?"
        description="Once it's converted it'll no longer get updated by editing the global section"
        actions={[
          {
            title: 'Cancel',
            onClick: () => {
              track('editor_modal_convert_global_to_local_cancelled');
              setConvertGlobalToLocalModalOpen(false);
              setActiveWebsiteUpdate(undefined);
            },
            type: 'secondary',
          },
          {
            title: 'Convert to Normal',
            type: 'primary',
            color: 'negative',
            onClick: () => {
              track('editor_modal_section_converted_global_to_local', {
                section_id: activeWebsiteUpdate?.sectionId,
              });
              const wu = activeWebsiteUpdate;
              setConvertGlobalToLocalModalOpen(false);
              setActiveWebsiteUpdate(undefined);
              if (!wu) return;
              updateWebsiteByEvent(wu);
            },
          },
        ]}
        type={'warn'}
      />

      {/* Convert Local to Global Modal */}
      <AlertModal
        open={!!convertLocalToGlobalModalOpen}
        onClose={() => {
          track('editor_modal_convert_local_to_global_cancelled');
          setConvertLocalToGlobalModalOpen(undefined);
          setActiveWebsiteUpdate(undefined);
        }}
        title="Create a Global Section"
        description="Turn this section into a reusable block you can place on any page. Any changes you make will update on all pages using it."
        actions={[
          {
            title: 'Cancel',
            onClick: () => {
              track('editor_modal_convert_local_to_global_cancelled');
              setConvertLocalToGlobalModalOpen(undefined);
              setActiveWebsiteUpdate(undefined);
            },
            type: 'secondary',
          },
          {
            title: 'Create & Set as Global Section',
            type: 'primary',
            loading: convertLocalToGlobalModalOpen?.creating,
            disabled: (convertLocalToGlobalModalOpen?.name?.length ?? 0) < 3,
            onClick: async () => {
              const wu = activeWebsiteUpdate;
              const section = website?.development.sections.find(
                (i) => i.id === wu?.sectionId,
              );
              if (!wu || !selectedAccount || !section) {
                setConvertLocalToGlobalModalOpen(undefined);
                return;
              }

              setConvertLocalToGlobalModalOpen({ creating: true });

              try {
                const newGlobalSection = await createGlobalSection(
                  selectedAccount.id,
                  section,
                  auth.currentUser?.uid ?? '',
                  convertLocalToGlobalModalOpen?.name ?? '',
                );

                track('editor_modal_section_converted_local_to_global', {
                  section_id: wu.sectionId,
                  global_section_id: newGlobalSection.id,
                  global_section_name: convertLocalToGlobalModalOpen?.name,
                });

                setActiveWebsiteUpdate(undefined);

                const newWU = {
                  ...wu,
                  content: {
                    global: { globalId: newGlobalSection.id },
                  },
                };

                updateWebsiteByEvent(newWU);
                setConvertLocalToGlobalModalOpen(undefined);
              } catch (e) {
                Sentry.captureException(e);
                track('editor_modal_convert_local_to_global_failed', {
                  section_id: wu.sectionId,
                  error: (e as Error).message,
                });
                show('Error creating global section', 'warn');
                setConvertLocalToGlobalModalOpen({ creating: false });
              }
            },
          },
        ]}
        type={'warn'}
      >
        <div className="pt-4 text-start">
          <Input
            title="Global Section Name"
            value={convertLocalToGlobalModalOpen?.name}
            onChange={(e: any) => {
              const newName = e.target.value;
              const currentState = convertLocalToGlobalModalOpen;
              setConvertLocalToGlobalModalOpen(
                currentState
                  ? { ...currentState, name: newName }
                  : { name: newName },
              );
            }}
          />
        </div>
      </AlertModal>

      {/* Discard Changes Confirmation Modal - for GlobalSection only */}
      {mode === 'globalSection' && (
        <AlertModal
          open={discardChangesConfirmModalOpen}
          onClose={() => setDiscardChangesConfirmModalOpen(false)}
          title="Discard Changes"
          description="Are you sure you want to discard all unpublished changes? This action cannot be undone."
          actions={[
            {
              title: 'Cancel',
              onClick: () => setDiscardChangesConfirmModalOpen(false),
              type: 'secondary',
            },
            {
              title: 'Discard',
              type: 'primary',
              color: 'negative',
              onClick: async () => {
                track('editor_modal_global_section_changes_discarded');
                if (selectedWebsiteId) {
                  await discardChanges(dynamicPageId ?? '', selectedWebsiteId);
                }
                setDiscardChangesConfirmModalOpen(false);
              },
            },
          ]}
          type={'warn'}
        />
      )}

      {/* Edit Quill Search Param Mention Modal */}
      <EditQuillSearchParamMentionModal
        mention={activeWebsiteUpdate?.content?.mention}
        open={editQuillSearchParamMentionModalOpen}
        onClose={() => {
          setEditQuillSearchParamMentionModalOpen(false);
        }}
        onSave={(newMention) => {
          const wu = activeWebsiteUpdate;
          if (!wu) return;

          const content = wu.content?.text;
          if (!content || typeof content !== 'object' || !content.ops) return;

          const newContentOps = content.ops.map((op: any) => {
            const insert = op.insert as any;
            const currentMention = insert?.['custom-value-mention'];
            if (currentMention && currentMention.id === newMention.id) {
              return {
                ...op,
                insert: {
                  ...insert,
                  'custom-value-mention': newMention,
                },
              };
            }
            return op;
          });

          updateWebsiteByEvent({
            ...wu,
            type: 'text',
            content: {
              ...content,
              ops: newContentOps,
            },
          });

          setEditQuillSearchParamMentionModalOpen(false);
        }}
      />

      {/* Create Form Modal */}
      <CreateFormModal
        form={
          editFormModalOpen &&
          (!editFormModalOpen.form.formType ||
            editFormModalOpen.form.formType === 'simple')
            ? editFormModalOpen?.form
            : undefined
        }
        formId={editFormModalOpen?.formId}
        open={
          (editFormModalOpen?.open ?? false) &&
          (!editFormModalOpen?.form.formType ||
            editFormModalOpen.form.formType === 'simple')
        }
        onClose={() => {
          setEditFormModalOpen(undefined);
          setActiveWebsiteUpdate(undefined);
        }}
      />

      {/* Edit HTML Action Modal */}
      <EditHtmlActionModal
        open={!!editHtmlActionModalOpen}
        onClose={() => setEditHtmlActionModalOpen(undefined)}
        popups={
          website?.development?.popups
            ?.filter((p) => p.enabled)
            ?.map((p) => ({ id: p.id, name: p.name })) ?? []
        }
        data={
          editHtmlActionModalOpen
            ? {
                action:
                  editHtmlActionModalOpen.action === 'form'
                    ? 'form'
                    : editHtmlActionModalOpen.action === 'popup'
                      ? 'popup'
                      : editHtmlActionModalOpen.url
                        ? 'link'
                        : 'none',
                url: editHtmlActionModalOpen.url || '',
                formId: editHtmlActionModalOpen.formId || '',
                popupId: editHtmlActionModalOpen.popupId || '',
                isConversion: editHtmlActionModalOpen.isConversion,
                openInNewTab: editHtmlActionModalOpen.openInNewTab,
                customEventData: editHtmlActionModalOpen.customEventData || {},
              }
            : undefined
        }
        onSave={(data) => {
          // Create a WebsiteUpdate event for editing HTML action
          if (
            !selectedAccount ||
            !selectedWebsiteId ||
            !dynamicPageId ||
            !editHtmlActionModalOpen
          ) {
            return;
          }

          const websiteUpdate: WebsiteUpdate = {
            id: `edit-html-action-${Date.now()}`,
            accountId: selectedAccount.id,
            campaignId: dynamicPageId,
            websiteId: selectedWebsiteId,
            sectionId: editHtmlActionModalOpen.sectionId,
            propKey: editHtmlActionModalOpen.elementPath,
            content: {
              ...data,
              elementData: editHtmlActionModalOpen,
            },
            type: 'editHtmlAction',
            subitems: [],
            before: undefined,
          };

          updateWebsiteByEvent(websiteUpdate);
          setEditHtmlActionModalOpen(undefined);
        }}
      />

      {/* Replace Form Modal */}
      <ReplaceFormModal
        open={replaceFormModalOpen?.open ?? false}
        currentFormId={replaceFormModalOpen?.currentFormId ?? ''}
        onClose={() => setReplaceFormModalOpen(undefined)}
        onSave={(newFormId) => {
          const wu = activeWebsiteUpdate;
          if (!wu) return;
          if (
            !selectedAccount ||
            !selectedWebsiteId ||
            !replaceFormModalOpen?.sectionId
          ) {
            return;
          }

          updateWebsiteByEvent({
            ...wu,
            content: { formId: newFormId.formId || newFormId.quizId },
          });
          setReplaceFormModalOpen(undefined);
        }}
      />

      <CreateCTAButtonModal
        open={editCTAButtonModalOpen}
        onClose={() => {
          setEditCTAButtonModalOpen(false);
        }}
        item={
          editCTAButtonModalOpen
            ? getItemFromSection(
                website?.development.sections.find(
                  (i) => i.id === activeWebsiteUpdate?.sectionId,
                ),
                activeWebsiteUpdate?.propKey ?? '',
                activeWebsiteUpdate?.subitems,
              )
            : undefined
        }
        onSave={(button) => {
          setEditCTAButtonModalOpen(false);
          const wu = activeWebsiteUpdate;
          if (!wu) return;

          updateWebsiteByEvent({ ...wu, content: button });
        }}
      />

      {/* Edit Stepper Form Modal */}
      <EditStepperFormModal
        form={
          editFormModalOpen?.form.formType === 'stepper'
            ? editFormModalOpen?.form
            : undefined
        }
        formId={editFormModalOpen?.formId}
        open={
          (editFormModalOpen?.open ?? false) &&
          editFormModalOpen?.form.formType === 'stepper'
        }
        onClose={() => {
          setEditFormModalOpen(undefined);
          setActiveWebsiteUpdate(undefined);
        }}
      />

      {/* Edit HTML Stepper Form Modal */}
      <HtmlStepperEditorModal
        initialFormId={editHtmlStepperModalOpen?.formId ?? ''}
        quizId={editHtmlStepperModalOpen?.quizId ?? ''}
        open={editHtmlStepperModalOpen?.open ?? false}
        onClose={() => {
          setEditHtmlStepperModalOpen(undefined);
          setActiveWebsiteUpdate(undefined);
        }}
      />

      {/* Edit Global Section Modal (nested editor) */}
      {mode !== 'globalSection' && (
        <NewGlobalSectionEditorModal
          open={editGlobalSectionModalOpen?.open ?? false}
          globalSectionId={editGlobalSectionModalOpen?.globalSectionId ?? ''}
          onClose={() => {
            setEditGlobalSectionModalOpen(undefined);
          }}
        />
      )}

      {/* Editor Popup for Section Editing */}
      <EditorPopup
        open={sidebarOpen}
        mode={activeWebsiteUpdate?.sectionId ? 'edit' : 'list'}
        section={
          sidebarOpen
            ? (website?.development.sections.find(
                (i) => i.id === activeWebsiteUpdate?.sectionId,
              ) ??
              website?.development.banners?.find(
                (i) => i.id === activeWebsiteUpdate?.sectionId,
              ))
            : undefined
        }
        website={website}
        websiteId={selectedWebsiteId || ''}
        onUpdateWebsiteEvent={updateWebsiteByEvent}
        onClose={() => setSidebarOpen(false)}
        onChange={(section) => {
          if (!dynamicPageId) return;

          const wu = createWebsiteUpdate(
            dynamicPageId,
            selectedWebsiteId || '',
            section.id,
            '',
            section,
            'editSection',
            [],
            website?.development
              ? JSON.parse(JSON.stringify(website?.development))
              : undefined,
          );

          if (!wu) return;
          updateWebsiteByEvent(wu);
        }}
      />

      {/* Pending Agent Changes Modal */}
      <AlertModal
        open={pendingAgentChangesModalOpen?.open ?? false}
        onClose={() => {
          track('editor_modal_pending_agent_changes_closed', {
            pending_changes_count: agentChanges.filter(
              (c) => c.status === 'pending',
            ).length,
          });
          setPendingAgentChangesModalOpen(undefined);
        }}
        title="You have pending AI agent changes"
        description={`There are ${agentChanges.filter((c) => c.status === 'pending').length} pending changes from the AI agent that need your review before publishing. Would you like to approve all, reject all, or review them individually?`}
        actions={[
          {
            title: 'Review individually',
            onClick: () => {
              track('editor_modal_agent_changes_review_individually', {
                pending_changes_count: agentChanges.filter(
                  (c) => c.status === 'pending',
                ).length,
              });
              setPendingAgentChangesModalOpen(undefined);
            },
            type: 'secondary',
          },
          {
            title: 'Reject all',
            type: 'primary',
            color: 'negative',
            loading: pendingAgentChangesModalOpen?.loading === 'rejecting',
            onClick: async () => {
              track('editor_modal_agent_changes_rejected_all', {
                rejected_count: agentChanges.filter(
                  (c) => c.status === 'pending',
                ).length,
              });
              await agentDiscardAllChanges();
              setPendingAgentChangesModalOpen(undefined);
            },
          },
          {
            title: 'Approve all',
            type: 'primary',
            color: 'positive',
            loading: pendingAgentChangesModalOpen?.loading === 'approving',
            onClick: async () => {
              track('editor_modal_agent_changes_approved_all', {
                approved_count: agentChanges.filter(
                  (c) => c.status === 'pending',
                ).length,
              });
              await agentAcceptAllChanges();
              setPendingAgentChangesModalOpen(undefined);
            },
          },
        ]}
        type={'info'}
      />
    </>
  );
};
